// Catálogo de reportes — Portal Admin VentasLuga (F3 · pista Retail 🔵).
// Mockup: `surekeep/mockups/backoffice/portal-admin-vl-reportes.html`.
//
// Generar es SÍNCRONO: `POST /backoffice/reports/{id}/run` responde `201` con el CSV en el cuerpo y
// la pantalla lo baja en ese momento. El log no ofrece re-descarga — `downloadUrl` viaja `null`.
//
// ✂️ La sección 2 del mockup ("Conciliación", RVL-07) y el reporte cross-border (RVL-06) NO se
// construyen: quedaron fuera del corte.
import { useState } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ErrorState, Skeleton, usePermissions } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import type { ReportFormat } from '@/api/services/retail-wizard/backoffice';
import { useReportCatalog, useRunReport } from './api/useReportes';
import { REPORTS_PERMISSIONS } from './permissions';
import { NoSource } from '@/shared/patterns/NoSource';
import { ReportDetailModal } from './components/ReportDetailModal';
import { downloadReportFile } from './lib/downloadReportFile';
import { ReportPeriodPicker } from './components/ReportPeriodPicker';
import { reportPeriodWindow, type ReportPeriodSelection } from './lib/reportPeriod';
import {
    formatInt,
    PERIODICITY_CHIP,
    PERIODICITY_LABEL,
    runnableFormats,
    RUN_STATUS_CHIP,
    RUN_STATUS_LABEL,
} from './lib/reportesFormat';

function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}

function Kpi({
    label,
    value,
    sub,
    valueClass,
    title,
}: {
    label: string;
    value: string | number;
    sub: string;
    valueClass?: string;
    title?: string;
}) {
    return (
        <div
            className="flex flex-none items-baseline gap-1.5 border-r border-border px-3.5 last:border-r-0"
            title={title}
        >
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                {label}
            </span>
            <span className={cn('text-[14px] font-semibold tabular-nums', valueClass)}>
                {value}
            </span>
            <span className="text-[10px] text-muted-foreground">{sub}</span>
        </div>
    );
}

/** Por qué el botón de generar está apagado. Se dice: un botón muerto sin motivo se lee como bug. */
const NO_RUN_PERMISSION_HINT =
    'Tu rol no incluye generar reportes. Pídelo al administrador de VentasLuga.';

export function ReportesPage() {
    const navigate = useNavigate();
    const { reporte } = useSearch({ from: '/_backoffice/retail-admin/$section' });
    const [pendingRun, setPendingRun] = useState<string | null>(null);
    const [period, setPeriod] = useState<ReportPeriodSelection>({ preset: '30d' });

    const catalog = useReportCatalog();
    const runReport = useRunReport();
    // Generar es `retail.report.run`, distinto del `retail.report.list` con el que se entra aquí.
    const { hasPermission } = usePermissions();
    const canRun = hasPermission(REPORTS_PERMISSIONS.run);

    const openDetail = (reportId: string | null) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'reportes' },
            search: { reporte: reportId ?? undefined },
            replace: true,
        });
    };

    const run = (reportId: string, format: ReportFormat) => {
        // Guardia, no solo el botón apagado: sin el permiso el POST vuelve 403 igual.
        if (!canRun) return;
        const dataWindow = reportPeriodWindow(period);
        if (!dataWindow) {
            toast.error('Elige un rango de fechas válido', {
                description: 'Indica el día inicial y el final; el inicial no puede ser posterior.',
            });
            return;
        }
        setPendingRun(reportId);
        runReport.mutate(
            { reportId, format, period: dataWindow },
            {
                onSuccess: (result) => {
                    setPendingRun(null);
                    if (!downloadReportFile(result)) {
                        toast.error('El reporte no se pudo generar', {
                            description:
                                result.failureReason ??
                                'La corrida quedó registrada en el log sin producir archivo.',
                        });
                        return;
                    }
                    const rows = result.rowCount;
                    toast.success('Reporte descargado', {
                        description:
                            rows === null
                                ? result.filename
                                : `${result.filename} · ${formatInt(rows)} ${rows === 1 ? 'fila' : 'filas'}`,
                    });
                },
                onError: (e) => {
                    setPendingRun(null);
                    toast.error(e instanceof Error ? e.message : 'No se pudo generar el reporte');
                },
            },
        );
    };

    const data = catalog.data;
    const error = asError(catalog.error);
    const selected = data?.reports.find((r) => r.reportId === reporte);
    // El `reportId` es también el orden del catálogo de M6 §5.1 (`RVL-01`…`RVL-05`), el que el
    // operador cita: ordenar por él es alfabético y respeta la numeración del documento.
    const reports = data
        ? [...data.reports].sort((a, b) => a.reportId.localeCompare(b.reportId))
        : [];
    // El log es cronológico por definición: lo más reciente primero.
    const recentRuns = data
        ? [...data.recentRuns].sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))
        : [];

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="mt-2 flex-none rounded-t-[10px] border border-border bg-card px-3 py-2 text-[11px] text-muted-foreground">
                ¿Qué reportes de ventas existen para VentasLuga? Los diarios y semanales corren
                automáticos en cron; los mensuales requieren corrida manual. Se exportan a CSV — PDF
                no está implementado. Referencia M6_OPERACIONES §5.1.
            </div>

            <div className="flex flex-none items-center overflow-x-auto border-x border-b border-border bg-card px-3 py-2">
                <Kpi label="Catálogo" value={data?.kpis.total ?? '—'} sub="reportes disponibles" />
                <Kpi
                    label="Diarios"
                    value={data?.kpis.daily ?? '—'}
                    sub="Cron 06:00-07:15"
                    valueClass="text-info"
                />
                <Kpi
                    label="Semanales"
                    value={data?.kpis.weekly ?? '—'}
                    sub="Cron lunes"
                    valueClass="text-success"
                />
                <Kpi
                    label="Mensuales"
                    value={data?.kpis.monthly ?? '—'}
                    sub="corrida manual"
                    valueClass="text-warning"
                />
                <Kpi
                    label="Corridas 30d"
                    value={
                        data
                            ? `${data.kpis.runsTruncated ? '≥ ' : ''}${formatInt(data.kpis.runsLast30d)}`
                            : '—'
                    }
                    sub="todas las categorías"
                    title={
                        data?.kpis.runsTruncated
                            ? 'El log llegó topado en 50 corridas: este número es un piso, no el total.'
                            : undefined
                    }
                />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-b-[10px] border-x border-b border-border bg-background p-2">
                {error ? (
                    <ErrorState
                        title="No se pudo cargar el catálogo de reportes"
                        description={error.message}
                        onRetry={() => {
                            void catalog.refetch();
                        }}
                    />
                ) : (
                    <>
                        <section className="mb-2 rounded-lg border border-border bg-card">
                            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
                                <span className="text-[12px] font-semibold">1 · Ventas</span>
                                <ReportPeriodPicker value={period} onChange={setPeriod} />
                                <span className="text-[10px] text-muted-foreground">
                                    {canRun
                                        ? 'Por período · negocio · vendedor · producto · financiera'
                                        : NO_RUN_PERMISSION_HINT}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 p-2">
                                {catalog.isPending
                                    ? Array.from({ length: 4 }, (_unused, i) => (
                                          <div
                                              key={i}
                                              className="rounded-md border border-border p-3"
                                          >
                                              <Skeleton lines={4} />
                                          </div>
                                      ))
                                    : reports.map((report) => (
                                          <button
                                              key={report.reportId}
                                              type="button"
                                              onClick={() => {
                                                  openDetail(report.reportId);
                                              }}
                                              className="rounded-md border border-border bg-card p-3 text-left transition-colors hover:bg-accent/40"
                                          >
                                              <div className="flex items-start justify-between gap-2">
                                                  <div className="min-w-0">
                                                      <div className="text-[12px] font-semibold">
                                                          {report.name}
                                                      </div>
                                                      <div className="font-mono text-[9.5px] text-muted-foreground">
                                                          {/* eslint-disable-next-line no-restricted-syntax -- código de negocio del reporte (`RVL-01`), el que se cita en los documentos */}
                                                          {report.reportId} · {report.reference}
                                                      </div>
                                                  </div>
                                                  <span
                                                      className={cn(
                                                          'flex-none rounded-full px-1.5 py-px text-[9px] font-bold',
                                                          PERIODICITY_CHIP[report.periodicity],
                                                      )}
                                                  >
                                                      {PERIODICITY_LABEL[report.periodicity]}
                                                  </span>
                                              </div>
                                              <p className="mt-1.5 text-[10.5px] leading-snug text-muted-foreground">
                                                  {report.description}
                                              </p>
                                              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[9.5px] text-muted-foreground">
                                                  <span>
                                                      {report.lastRunAt
                                                          ? `Última corrida ${formatDateTimeMx(report.lastRunAt)}`
                                                          : 'Nunca se ha corrido'}
                                                  </span>
                                                  <span>·</span>
                                                  <span>
                                                      Volumen típico:{' '}
                                                      <NoSource reason="El endpoint del catálogo no manda volumen ni tamaño estimado. El número de filas aparece al generar el reporte." />
                                                  </span>
                                              </div>
                                              <div className="mt-2 flex flex-wrap gap-1.5">
                                                  {runnableFormats(report.formats).map((format) => {
                                                      const busy = pendingRun === report.reportId;
                                                      const blocked = !canRun || busy;
                                                      return (
                                                          <span
                                                              key={format}
                                                              role="button"
                                                              tabIndex={canRun ? 0 : -1}
                                                              aria-disabled={blocked}
                                                              onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  if (blocked) return;
                                                                  run(report.reportId, format);
                                                              }}
                                                              onKeyDown={(e) => {
                                                                  if (
                                                                      e.key === 'Enter' ||
                                                                      e.key === ' '
                                                                  ) {
                                                                      e.stopPropagation();
                                                                      if (blocked) return;
                                                                      run(report.reportId, format);
                                                                  }
                                                              }}
                                                              className={cn(
                                                                  'inline-flex items-center rounded bg-primary px-2 py-[3px] text-[9.5px] font-semibold text-primary-foreground transition-opacity',
                                                                  blocked
                                                                      ? 'cursor-not-allowed opacity-60'
                                                                      : 'cursor-pointer hover:opacity-90',
                                                              )}
                                                              title={
                                                                  canRun
                                                                      ? 'Genera el archivo y lo descarga en el momento'
                                                                      : NO_RUN_PERMISSION_HINT
                                                              }
                                                          >
                                                              {busy
                                                                  ? 'Generando…'
                                                                  : `Descargar ${format}`}
                                                          </span>
                                                      );
                                                  })}
                                              </div>
                                          </button>
                                      ))}
                            </div>
                        </section>

                        <section className="rounded-lg border border-border bg-card">
                            <div className="flex items-baseline justify-between gap-3 border-b border-border px-3 py-2">
                                <span className="text-[12px] font-semibold">
                                    2 · Log de corridas
                                </span>
                                <span className="text-right text-[10px] text-muted-foreground">
                                    Últimas corridas dentro del alcance del Admin VL · el log no
                                    guarda el archivo: para tenerlo hay que volver a generarlo
                                </span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[760px] border-collapse text-[11px]">
                                    <thead>
                                        <tr className="border-b border-border text-left text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                                            <th scope="col" className="px-2 py-1.5">
                                                Fecha y hora
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Reporte
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Formato
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Estado
                                            </th>
                                            <th scope="col" className="px-2 py-1.5 text-right">
                                                Filas
                                            </th>
                                            <th scope="col" className="px-2 py-1.5">
                                                Solicitó
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="whitespace-nowrap">
                                        {catalog.isPending
                                            ? Array.from({ length: 6 }, (_unused, i) => (
                                                  <tr key={i}>
                                                      {Array.from({ length: 6 }, (_u, j) => (
                                                          <td key={j} className="px-2 py-1.5">
                                                              <Skeleton className="h-3" />
                                                          </td>
                                                      ))}
                                                  </tr>
                                              ))
                                            : recentRuns.map((entry) => (
                                                  <tr
                                                      key={entry.runId}
                                                      className="border-b border-border/50"
                                                  >
                                                      <td className="px-2 py-1.5 tabular-nums">
                                                          {formatDateTimeMx(entry.requestedAt)}
                                                      </td>
                                                      <td className="px-2 py-1.5">
                                                          <div
                                                              title={entry.reportName || undefined}
                                                              className="max-w-[260px] truncate font-semibold"
                                                          >
                                                              {entry.reportName || (
                                                                  <NoSource reason="Esta corrida es de un reporte que ya no está en el catálogo." />
                                                              )}
                                                          </div>
                                                          <div className="font-mono text-[9.5px] text-muted-foreground">
                                                              {/* eslint-disable-next-line no-restricted-syntax -- código de negocio del reporte */}
                                                              {entry.reportId}
                                                          </div>
                                                      </td>
                                                      <td className="px-2 py-1.5">
                                                          <span className="rounded border border-border px-1.5 py-px text-[9px] font-semibold">
                                                              {entry.format}
                                                          </span>
                                                      </td>
                                                      <td className="px-2 py-1.5">
                                                          <span
                                                              className={cn(
                                                                  'inline-flex rounded-full px-1.5 py-px text-[9px] font-bold',
                                                                  RUN_STATUS_CHIP[entry.status],
                                                              )}
                                                          >
                                                              {RUN_STATUS_LABEL[entry.status]}
                                                          </span>
                                                          {entry.failureReason ? (
                                                              <span
                                                                  title={entry.failureReason}
                                                                  className="ml-1 inline-block max-w-[180px] truncate align-middle text-[9.5px] text-destructive"
                                                              >
                                                                  {entry.failureReason}
                                                              </span>
                                                          ) : null}
                                                      </td>
                                                      <td className="px-2 py-1.5 text-right tabular-nums">
                                                          {entry.rowCount === null ? (
                                                              <NoSource reason="La corrida no terminó bien: el silo no reporta filas." />
                                                          ) : (
                                                              formatInt(entry.rowCount)
                                                          )}
                                                      </td>
                                                      <td className="px-2 py-1.5">
                                                          {entry.requestedByName ?? (
                                                              <NoSource reason="El endpoint manda el id del usuario, no su nombre." />
                                                          )}
                                                      </td>
                                                  </tr>
                                              ))}
                                    </tbody>
                                </table>
                            </div>
                            {!catalog.isPending && recentRuns.length === 0 ? (
                                <div className="py-8 text-center text-[12px] text-muted-foreground">
                                    Todavía no hay corridas registradas.
                                </div>
                            ) : null}
                        </section>
                    </>
                )}
            </div>

            <ReportDetailModal
                report={selected}
                onClose={() => {
                    openDetail(null);
                }}
                onRun={run}
                isRunning={pendingRun !== null}
                canRun={canRun}
                noRunReason={NO_RUN_PERMISSION_HINT}
            />
        </div>
    );
}
